import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Pencil, Plus, Scale, Scroll, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { TipoDeExoneracion } from '@/shared/api/tipos';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { Interruptor } from '@/shared/ui/Interruptor';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { PestanasDeExoneraciones } from './PestanasDeExoneraciones';

export function PaginaTiposDeExoneracion() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<TipoDeExoneracion | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<TipoDeExoneracion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['tipos-exoneracion'],
    queryFn: () => api.get<TipoDeExoneracion[]>('/tipos-exoneracion'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['tipos-exoneracion'] });
  }

  function alFallar(causa: unknown, respaldo: string) {
    const mensaje = causa instanceof ErrorDeApi ? causa.message : respaldo;
    setError(mensaje);
    notificar.error(mensaje);
  }

  const cambiarEstado = useMutation({
    mutationFn: (t: TipoDeExoneracion) =>
      api.put<TipoDeExoneracion>(`/tipos-exoneracion/${t.id}`, {
        codigo: t.codigo,
        nombre: t.nombre,
        porcentajePorOmision: t.porcentajePorOmision,
        baseLegal: t.baseLegal,
        exigeGrado: t.exigeGrado,
        activo: !t.activo,
        orden: t.orden,
      }),
    onSuccess: refrescar,
    onError: (causa) => alFallar(causa, 'No se pudo cambiar el estado'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/tipos-exoneracion/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Tipo de exoneración eliminado');
    },

    onError: (causa) => alFallar(causa, 'No se pudo eliminar'),
  });

  return (
    <>
      <EncabezadoDePagina
        icono={Scroll}
        color="indigo"
        titulo="Tipos de exoneración"
        subtitulo="Las categorías que concede la ordenanza"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo tipo"
            aria-label="Nuevo tipo de exoneración"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <PestanasDeExoneraciones />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">
            Cambiar el porcentaje de un tipo no cambia lo ya concedido.
          </span>{' '}
          Cada exoneración guardó el suyo al concederse, bajo la norma de entonces. Si una reforma
          sube el descuento, las vigentes siguen con el anterior hasta que alguien las revise.
        </p>
      </div>

      {tipos.isPending ? (
        <Cargando
          titulo="Cargando tipos de exoneración"
          detalle="Obteniendo registros"
          alto="h-48"
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <div className="hidden items-center gap-4 border-b border-armazon-100 bg-armazon-50/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 lg:flex">
            <span className="w-8 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 basis-56">Tipo</span>
            <span className="min-w-0 flex-[2] basis-64">Base legal</span>
            <span className="w-24 shrink-0 text-right">Concede</span>
            <span className="w-24 shrink-0 text-center">Estado</span>
            <span className="w-16 shrink-0" aria-hidden />
          </div>

          <div className="divide-y divide-armazon-100">
            {(tipos.data ?? []).map((t) => (
              <div
                key={t.id}
                className="flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60"
              >
                <span className="importe grid size-8 shrink-0 place-items-center rounded-lg bg-armazon-100 text-xs font-semibold text-armazon-600">
                  {t.orden}
                </span>

                <div className="min-w-0 flex-1 basis-56">
                  <p className="truncate text-sm font-medium uppercase text-armazon-800">
                    {t.nombre}
                  </p>

                  {t.exigeGrado ? (
                    <span
                      className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-armazon-500"
                      title="Al registrarla se anota además el grado del carné del CONADIS"
                    >
                      <Scale className="size-2.5 shrink-0" aria-hidden />
                      Anota el grado del carné
                    </span>
                  ) : (
                    <span className="mt-0.5 block text-[10px] uppercase text-armazon-300">
                      Sin grado
                    </span>
                  )}
                </div>

                <p
                  className="min-w-0 flex-[2] basis-64 truncate text-xs uppercase text-armazon-600"
                  title={t.baseLegal}
                >
                  {t.baseLegal}
                </p>

                <p className="importe w-24 shrink-0 text-right text-sm font-bold text-indigo-600">
                  {t.porcentajePorOmision} %
                </p>

                <div className="flex w-24 shrink-0 justify-center">
                  <Interruptor
                    activo={t.activo}
                    alCambiar={() => cambiarEstado.mutate(t)}
                    etiqueta={`Activar o desactivar ${t.nombre}`}
                    deshabilitado={cambiarEstado.isPending}
                  />
                </div>

                <div className="flex w-16 shrink-0 items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => setEditando(t)}
                    title="Editar"
                    aria-label={`Editar ${t.nombre}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="size-[15px]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAEliminar(t)}
                    title="Eliminar"
                    aria-label={`Eliminar ${t.nombre}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                  >
                    <Trash2 className="size-[15px]" aria-hidden />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogoDeTipo
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        tipo={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar tipo de exoneración"
        descripcion="Solo se eliminan los que nunca concedieron nada:"
        detalle={
          aEliminar ? `${aEliminar.nombre} · ${aEliminar.porcentajePorOmision} %` : undefined
        }
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function DialogoDeTipo({
  abierto,
  tipo,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  tipo: TipoDeExoneracion | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = tipo !== null;

  const [codigo, setCodigo] = useState(tipo?.codigo ?? '');
  const [nombre, setNombre] = useState(tipo?.nombre ?? '');
  const [porcentaje, setPorcentaje] = useState(
    tipo === null ? '50' : String(tipo.porcentajePorOmision),
  );
  const [baseLegal, setBaseLegal] = useState(tipo?.baseLegal ?? '');
  const [exigeGrado, setExigeGrado] = useState(tipo?.exigeGrado ?? false);
  const [orden, setOrden] = useState(tipo === null ? '0' : String(tipo.orden));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = codigo.trim() !== '' && nombre.trim() !== '' && baseLegal.trim() !== '';

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const cuerpo = {
        codigo: codigo.trim(),
        nombre: nombre.trim(),
        porcentajePorOmision: Number(porcentaje),
        baseLegal: baseLegal.trim(),
        exigeGrado,
        activo: tipo?.activo ?? true,
        orden: Number(orden) || 0,
      };

      if (editando) {
        await api.put<TipoDeExoneracion>(`/tipos-exoneracion/${tipo.id}`, cuerpo);
      } else {
        await api.post<TipoDeExoneracion>('/tipos-exoneracion', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Tipo ${editando ? 'actualizado' : 'creado'}: ${cuerpo.nombre}`);
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el tipo';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar tipo' : 'Nuevo tipo de exoneración'}
      subtitulo={editando ? tipo.nombre : 'Una categoría que concede la ordenanza'}
      icono={Scroll}
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Nombre" requerido ayuda="COMO SE VE EN LAS LISTAS Y EN EL FORMULARIO">
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={claseEntrada}
              placeholder="ENFERMEDAD CATASTRÓFICA"
              autoFocus
            />
          </Campo>

          <Campo
            etiqueta="Código"
            requerido
            ayuda={
              editando
                ? 'NO SE PUEDE CAMBIAR: LO REFERENCIAN LAS EXONERACIONES YA CONCEDIDAS'
                : 'MINÚSCULAS, SIN TILDES NI ESPACIOS: enfermedad_catastrofica'
            }
          >
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              className={`${claseEntrada} importe ${editando ? 'bg-armazon-50' : ''}`}
              placeholder="enfermedad_catastrofica"
              readOnly={editando}
            />
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Descuento que concede"
            requerido
            ayuda="SE PROPONE AL REGISTRAR UNA EXONERACIÓN DE ESTE TIPO"
          >
            <input
              value={porcentaje}
              onChange={(e) => setPorcentaje(e.target.value)}
              type="number"
              step="0.01"
              min={0.01}
              max={100}
              className={`${claseEntrada} importe`}
            />
          </Campo>

          <Campo etiqueta="Orden" ayuda="MENOR VA PRIMERO EN LAS LISTAS">
            <input
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
              type="number"
              min={0}
              className={`${claseEntrada} importe`}
            />
          </Campo>
        </div>

        <Campo
          etiqueta="Base legal"
          requerido
          ayuda="EL ARTÍCULO Y LITERAL QUE LA CONCEDE. UN TIPO SIN NORMA DETRÁS ES UNA VÍA PARA DESCUENTOS QUE NADIE PUEDE JUSTIFICAR."
        >
          <input
            value={baseLegal}
            onChange={(e) => setBaseLegal(e.target.value)}
            className={claseEntrada}
            placeholder="Art. 12 literal c) — Ordenanza GIRS Latacunga, RO EE 1223 de 02-abr-2026"
          />
        </Campo>

        <div className="flex items-start gap-3 rounded-xl border border-armazon-200 p-3">
          <span className="mt-0.5 shrink-0">
            <Interruptor
              activo={exigeGrado}
              alCambiar={() => setExigeGrado((v) => !v)}
              etiqueta="Pide el grado del carné"
            />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium uppercase text-armazon-900">
              Pide el grado del carné
            </span>
            <span className="mt-0.5 block text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
              Al registrar una exoneración de este tipo se anota además el grado que consta en el
              carné. Es un dato, no el descuento: el descuento es el de arriba.
            </span>
          </span>
        </div>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar={editando ? 'Actualizar tipo' : 'Crear tipo'}
        />
      </form>
    </Modal>
  );
}
